import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import FileUploader from '@/components/FileUploader';
import { ACCEPTED_FILES_CSV, ACCEPTED_FILES_PDF } from '@/constants/common';
import { REPORTS } from '@/constants/reports.constants';
import useReports from '@/pages/Reports/Reports.hooks';
import type { ReportType } from '@/pages/Reports/Reports.types';

import {
  FREQUENCY_OPTIONS,
  REPORT_DEFAULT_VALUES,
  REPORT_TYPES
} from './ReportsDialog.constants';

const ReportsDialog = ({
  open,
  setOpen
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const {
    createReport,
    createSuccess,
    orgHasEdrProducts,
    orgHasVmpProducts,
    selectedOrganizationId
  } = useReports();

  const form = useForm({
    defaultValues: REPORT_DEFAULT_VALUES as ReportType
  });
  const { control, handleSubmit } = form;

  const orgReportTypes = REPORT_TYPES.filter((option) => {
    return (
      (option.label !== (REPORTS.EDR as string) &&
        option.label !== (REPORTS.VMP as string)) ||
      (option.label === (REPORTS.EDR as string) && orgHasEdrProducts) ||
      (option.label === (REPORTS.VMP as string) && orgHasVmpProducts)
    );
  });

  const handleUpload = (data: ReportType) => {
    const dataCopy = { ...data, organization_id: selectedOrganizationId };
    const newFile = dataCopy.attachments[0];

    if (newFile.name.includes('pdf') || newFile.name.includes('csv')) {
      createReport(dataCopy as ReportType);
    }
  };

  useEffect(() => {
    if (createSuccess) {
      setOpen(false);
      form.reset(REPORT_DEFAULT_VALUES);
    }
  }, [createSuccess, setOpen]);

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={() => {
        setOpen(false);
        form.reset(REPORT_DEFAULT_VALUES);
      }}
      open={open}
    >
      <DialogTitle>Upload Report</DialogTitle>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(handleUpload)} noValidate>
          <DialogContent>
            <Stack sx={{ py: 2 }} spacing={2}>
              <div>
                <InputLabel sx={{ pb: 0.5 }}>Name</InputLabel>
                <Controller
                  control={control}
                  name="report_name"
                  rules={{
                    required: true
                  }}
                  render={({ field, formState }) => (
                    <>
                      <TextField
                        placeholder="Enter name"
                        fullWidth
                        error={!!formState.errors.report_name}
                        {...field}
                      />
                    </>
                  )}
                />
              </div>
              <div>
                <InputLabel sx={{ pb: 1 }}>Report Type</InputLabel>
                <Controller
                  control={control}
                  name="report_type_id"
                  rules={{
                    required: true
                  }}
                  render={({ field, formState }) => (
                    <ButtonGroup
                      id="report-type-button-group"
                      color={
                        formState.errors.report_type_id ? 'error' : 'primary'
                      }
                      variant="outlined"
                    >
                      {orgReportTypes.map((option) => (
                        <Button
                          variant={
                            field.value === option.id ? 'contained' : 'outlined'
                          }
                          key={option.id}
                          {...field}
                          onClick={() => {
                            field.onChange(option.id);
                          }}
                          value={option.id}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </ButtonGroup>
                  )}
                />
              </div>
              <div>
                <InputLabel sx={{ pb: 0.5 }}>Report Frequency</InputLabel>
                <Controller
                  control={control}
                  name="report_frequency_id"
                  render={({ field }) => (
                    <Select fullWidth {...field}>
                      {FREQUENCY_OPTIONS.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </div>
              <FileUploader
                acceptedTypes={[...ACCEPTED_FILES_CSV, ...ACCEPTED_FILES_PDF]}
                acceptedTypesButtonLabel="pdf, csv"
                singleFile
                errorMessage="Please upload a PDF or CSV file"
                isRequired
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpen(false);
                form.reset(REPORT_DEFAULT_VALUES);
              }}
              color="primary"
            >
              Cancel
            </Button>
            <Button color="primary" type="submit" variant="contained">
              Upload
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default ReportsDialog;
